import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Users, Heart, Lock, Crown, DollarSign, Radio, Send, Zap, Shield, ArrowLeft, Film, Timer, Clock } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { creatorAPI, walletAPI, mediaAPI } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';
import ShareButton from '../components/shared/ShareButton';
import ReportButton from '../components/shared/ReportButton';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

function MediaItem({ path }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const isVideo = path?.match(/\.(mp4|webm|mov)$/i);
  useEffect(() => {
    if (!path) return;
    const token = localStorage.getItem('mmarketing_token');
    fetch(`${API_BASE}/api/media/files/${path}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(b => setBlobUrl(URL.createObjectURL(b)))
      .catch(() => {});
    return () => { if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [path]);
  if (!blobUrl) return <div className="bg-zinc-800 animate-pulse h-48" />;
  if (isVideo) return <video src={blobUrl} controls className="w-full max-h-64 object-cover bg-black" />;
  return <img src={blobUrl} alt="" className="w-full max-h-64 object-cover" />;
}

export default function CreatorProfilePage() {
  const { userId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [walletBal, setWalletBal] = useState(0);

  // Post creation (for creator)
  const [newPost, setNewPost] = useState({ title: '', content: '', is_ppv: false, ppv_price: 5, tier_required: 'standard' });
  const [posting, setPosting] = useState(false);

  const isOwnProfile = user?.user_id === userId;

  useEffect(() => {
    const fetch = async () => {
      try {
        const [pRes, wRes] = await Promise.all([
          creatorAPI.getProfile(userId),
          walletAPI.getBalance().catch(() => ({ data: { balance: 0 } })),
        ]);
        setProfile(pRes.data);
        setWalletBal(wRes.data.balance || 0);
        if (user) {
          const postsRes = await creatorAPI.getPosts(userId);
          setPosts(postsRes.data);
        }
      } catch { toast.error('Profile not found'); navigate('/creators'); }
      finally { setLoading(false); }
    };
    fetch();
  }, [userId, user, navigate]);

  const handleCreatePost = async () => {
    if (!newPost.content.trim()) { toast.error('Content required'); return; }
    setPosting(true);
    try {
      const res = await creatorAPI.createPost(newPost);
      setPosts([res.data, ...posts]);
      setNewPost({ title: '', content: '', is_ppv: false, ppv_price: 5, tier_required: 'standard' });
      toast.success('Posted!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setPosting(false); }
  };

  const handleUnlock = async (postId, price) => {
    try {
      await creatorAPI.unlockPost(postId);
      toast.success('Unlocked!');
      const postsRes = await creatorAPI.getPosts(userId);
      setPosts(postsRes.data);
      const wRes = await walletAPI.getBalance();
      setWalletBal(wRes.data.balance || 0);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to unlock'); }
  };

  const handleLike = async (postId) => {
    try {
      await creatorAPI.likePost(postId);
      setPosts(posts.map(p => p.id === postId ? { ...p, likes: (p.likes || 0) + 1 } : p));
    } catch {}
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-zinc-950"><div className="w-8 h-8 border-2 border-pink-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!profile) return null;

  return (
    <div className="min-h-screen bg-zinc-950 pb-12" data-testid="creator-profile-page">
      {/* Banner */}
      <div className="h-48 bg-gradient-to-br from-pink-600 via-violet-600 to-indigo-800 relative">
        <div className="absolute inset-0 bg-black/20" />
        <Link to="/creators" className="absolute top-4 left-4 z-10 text-white/70 hover:text-white"><ArrowLeft size={24} /></Link>
        <div className="absolute top-4 right-4 z-10 flex items-center gap-3">
          <ShareButton contentType="creator" contentId={userId} title={profile?.display_name} />
          <ReportButton contentType="creator" contentId={userId} />
        </div>
      </div>

      {/* Profile Info */}
      <div className="max-w-3xl mx-auto px-4 -mt-16 relative z-10">
        <div className="flex items-end gap-4 mb-6">
          <div className="w-28 h-28 rounded-2xl bg-gradient-to-br from-pink-500 to-violet-500 flex items-center justify-center border-4 border-zinc-950 text-white text-4xl font-bold">
            {(profile.display_name || 'C')[0].toUpperCase()}
          </div>
          <div className="flex-1 pb-2">
            <h1 className="font-display font-bold text-2xl text-white" data-testid="creator-display-name">{profile.display_name || 'Creator'}</h1>
            <div className="flex items-center gap-3 text-sm text-zinc-400 mt-1">
              <span className="flex items-center gap-1"><Users size={14} /> {profile.subscriber_count || 0} subscribers</span>
              <span className="flex items-center gap-1"><Film size={14} /> {profile.post_count || 0} posts</span>
              <span className="flex items-center gap-1"><Shield size={14} className="text-green-400" /> E2E Encrypted</span>
            </div>
          </div>
        </div>

        {/* Bio */}
        {profile.bio && <p className="text-zinc-300 text-sm mb-6 leading-relaxed">{profile.bio}</p>}

        {/* Tags */}
        {profile.tags?.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-6">
            {profile.tags.map(tag => (
              <span key={tag} className="px-3 py-1 bg-zinc-800 rounded-full text-xs text-zinc-300 border border-zinc-700">{tag}</span>
            ))}
          </div>
        )}

        {/* Subscription Tiers */}
        {!isOwnProfile && profile.tiers && Object.keys(profile.tiers).length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
            {Object.entries(profile.tiers).map(([key, tier]) => (
              <div key={key} className={`p-4 rounded-2xl border transition-all hover:scale-[1.02] ${
                key === 'diamond' ? 'bg-gradient-to-br from-amber-950 to-yellow-950 border-amber-500/30' :
                key === 'vip' ? 'bg-gradient-to-br from-violet-950 to-purple-950 border-violet-500/30' :
                'bg-zinc-900 border-zinc-700'
              }`} data-testid={`tier-${key}`}>
                <div className="flex items-center gap-2 mb-2">
                  {key === 'diamond' && <Crown size={16} className="text-amber-400" />}
                  {key === 'vip' && <Zap size={16} className="text-violet-400" />}
                  {key === 'standard' && <Users size={16} className="text-zinc-400" />}
                  <span className="text-sm font-semibold text-white">{tier.label}</span>
                </div>
                <p className="font-display font-bold text-2xl text-white mb-1">{formatCurrency(tier.price)}<span className="text-xs text-zinc-400 font-normal">/mo</span></p>
                <p className="text-xs text-zinc-500 mb-3">
                  {key === 'standard' && 'Access all standard posts + chat'}
                  {key === 'vip' && 'VIP posts + priority DMs + live access'}
                  {key === 'diamond' && 'All content + 1-on-1 calls + custom requests'}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Creator Rooms */}
        {profile.rooms?.length > 0 && (
          <div className="mb-8">
            <h2 className="font-display font-bold text-lg text-white mb-3">Live Rooms</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {profile.rooms.map(room => (
                <Link key={room.id} to={`/creator/${room.id}`}>
                  <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 hover:border-pink-500/30 transition-all">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-white text-sm">{room.name}</span>
                      {room.is_live && <span className="flex items-center gap-1 px-2 py-0.5 bg-red-500/20 rounded-full text-xs text-red-400 font-bold"><span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" /> LIVE</span>}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-zinc-400">
                      <span>{formatCurrency(room.price_per_month)}/mo</span>
                      <span>{room.subscriber_count} subs</span>
                      <span>{room.category}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Content Feed */}
        <Tabs defaultValue="posts" className="w-full">
          <TabsList className="bg-zinc-900 border border-zinc-800 rounded-full p-1 mb-4">
            <TabsTrigger value="posts" className="rounded-full data-[state=active]:bg-white data-[state=active]:text-black text-sm text-zinc-400">Posts</TabsTrigger>
            {isOwnProfile && <TabsTrigger value="create" className="rounded-full data-[state=active]:bg-pink-500 data-[state=active]:text-white text-sm text-zinc-400">New Post</TabsTrigger>}
          </TabsList>

          <TabsContent value="posts">
            {!user ? (
              <div className="text-center py-12 bg-zinc-900 rounded-2xl border border-zinc-800">
                <Lock className="mx-auto mb-3 text-zinc-500" size={40} />
                <p className="text-zinc-400 mb-3">Login to view content</p>
                <Link to="/login"><Button className="bg-pink-500 text-white rounded-full">Login</Button></Link>
              </div>
            ) : posts.length === 0 ? (
              <div className="text-center py-12 bg-zinc-900 rounded-2xl border border-zinc-800">
                <Film className="mx-auto mb-3 text-zinc-500" size={40} />
                <p className="text-zinc-400">No posts yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {posts.map((post, idx) => {
                  const hasTeaser = post.teaser_video_status === 'completed' && post.teaser_video_job_id;
                  const teaserProcessing = post.teaser_video_status === 'processing';
                  const isExpiring = post.is_expiring && post.expires_at;
                  const expiresAt = isExpiring ? new Date(post.expires_at) : null;
                  const timeLeft = expiresAt ? Math.max(0, expiresAt - new Date()) : 0;
                  const hoursLeft = Math.floor(timeLeft / (1000 * 60 * 60));
                  const minsLeft = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));

                  return (
                  <motion.div key={post.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.05 }}>
                    <div className={`bg-zinc-900 border rounded-2xl overflow-hidden ${post.locked ? 'border-zinc-700 opacity-80' : 'border-zinc-800'}`} data-testid={`post-${post.id}`}>
                      {/* Teaser Video */}
                      {hasTeaser && (
                        <div className="relative bg-black" data-testid={`teaser-video-${post.id}`}>
                          <video
                            src={`${process.env.REACT_APP_BACKEND_URL}/api/video/stream/${post.teaser_video_job_id}`}
                            controls
                            preload="metadata"
                            className="w-full max-h-64 object-cover"
                            poster=""
                          />
                          <div className="absolute top-2 left-2 flex items-center gap-1 bg-violet-500/80 text-white text-[10px] font-bold px-2 py-0.5 backdrop-blur-sm">
                            <Film size={10} /> FREE TEASER
                          </div>
                        </div>
                      )}
                      {teaserProcessing && (
                        <div className="bg-zinc-800 p-3 flex items-center gap-2 text-xs text-violet-400">
                          <div className="w-3 h-3 border border-violet-400 border-t-transparent rounded-full animate-spin" />
                          Teaser video generating...
                        </div>
                      )}

                      <div className="p-5">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs text-zinc-500">{new Date(post.created_at).toLocaleDateString()}</span>
                            {post.is_ppv && <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded-full text-xs font-bold">PPV</span>}
                            {post.tier_required !== 'free' && <span className="px-2 py-0.5 bg-violet-500/20 text-violet-400 rounded-full text-xs">{post.tier_required}</span>}
                            {isExpiring && timeLeft > 0 && (
                              <span className="flex items-center gap-1 px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded-full text-xs font-bold" data-testid={`expiry-badge-${post.id}`}>
                                <Timer size={10} /> {hoursLeft}h {minsLeft}m left
                              </span>
                            )}
                            {post.expired && <span className="px-2 py-0.5 bg-zinc-800 text-zinc-500 rounded-full text-xs">Expired</span>}
                          </div>
                          {!post.locked && (
                            <button onClick={() => handleLike(post.id)} className="flex items-center gap-1 text-xs text-zinc-400 hover:text-pink-400 transition-colors">
                              <Heart size={14} /> {post.likes || 0}
                            </button>
                          )}
                        </div>
                        {post.title && <h3 className="font-semibold text-white mb-2">{post.title}</h3>}
                        {/* Uploaded Media Gallery */}
                        {!post.locked && post.media_paths?.length > 0 && (
                          <div className="grid gap-2 mb-3" style={{ gridTemplateColumns: post.media_paths.length === 1 ? '1fr' : 'repeat(2, 1fr)' }}>
                            {post.media_paths.map((path, mi) => (
                              <MediaItem key={mi} path={path} />
                            ))}
                          </div>
                        )}
                        {post.locked && post.media_paths?.length > 0 && (
                          <div className="bg-zinc-800 p-4 mb-3 text-center text-xs text-zinc-500 flex items-center justify-center gap-2">
                            <Lock size={14} /> {post.media_paths.length} media file{post.media_paths.length > 1 ? 's' : ''} — unlock to view
                          </div>
                        )}
                        <p className="text-sm text-zinc-300 whitespace-pre-wrap leading-relaxed">{post.content}</p>
                        {post.locked && post.is_ppv && (
                          <Button onClick={() => handleUnlock(post.id, post.ppv_price)} className="mt-3 bg-amber-500 text-black font-bold rounded-full text-sm" data-testid={`unlock-${post.id}`}>
                            <Lock size={14} className="mr-1" /> Unlock for {formatCurrency(post.ppv_price)}
                          </Button>
                        )}
                        {post.locked && !post.is_ppv && (
                          <p className="mt-3 text-xs text-violet-400 flex items-center gap-1"><Lock size={12} /> Subscribe to view this content</p>
                        )}
                        {!post.locked && post.unlock_count > 0 && (
                          <p className="mt-2 text-xs text-zinc-500">{post.unlock_count} unlock{post.unlock_count > 1 ? 's' : ''}</p>
                        )}
                      </div>
                    </div>
                  </motion.div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {isOwnProfile && (
            <TabsContent value="create">
              <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6">
                <h3 className="font-display font-bold text-white mb-4">Create Post</h3>
                <div className="space-y-3">
                  <Input placeholder="Title (optional)" value={newPost.title} onChange={e => setNewPost({ ...newPost, title: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 text-white" data-testid="post-title" />
                  <Textarea placeholder="Write your content..." value={newPost.content} onChange={e => setNewPost({ ...newPost, content: e.target.value })}
                    rows={4} className="bg-zinc-800 border-zinc-700 text-white" data-testid="post-content" />
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
                      <input type="checkbox" checked={newPost.is_ppv} onChange={e => setNewPost({ ...newPost, is_ppv: e.target.checked })} className="rounded" />
                      <DollarSign size={14} /> Pay-Per-View
                    </label>
                    {newPost.is_ppv && (
                      <Input type="number" min={1} value={newPost.ppv_price} onChange={e => setNewPost({ ...newPost, ppv_price: parseFloat(e.target.value) || 1 })}
                        className="w-24 bg-zinc-800 border-zinc-700 text-white" placeholder="$" />
                    )}
                    <select value={newPost.tier_required} onChange={e => setNewPost({ ...newPost, tier_required: e.target.value })}
                      className="bg-zinc-800 border border-zinc-700 text-white text-sm rounded-lg px-3 py-2">
                      <option value="free">Free</option>
                      <option value="standard">Standard only</option>
                      <option value="vip">VIP+</option>
                      <option value="diamond">Diamond only</option>
                    </select>
                  </div>
                  <Button onClick={handleCreatePost} disabled={posting} className="w-full bg-gradient-to-r from-pink-500 to-violet-500 text-white font-bold rounded-xl" data-testid="submit-post-btn">
                    {posting ? 'Publishing...' : 'Publish Post'}
                  </Button>
                </div>
              </div>
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
}
